import React, { useRef, useEffect, useState, useMemo } from 'react';
import { hoverable } from '../themes/utils/responsiveHover';
import {
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  IconButton,
  Paper,
  useTheme,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  alpha,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { formatTimestamp, formatTimestampFull } from '../utils/timestampFormatter';


const ThreadList = ({
  threads,
  currentThreadId,
  onThreadSelect,
  onThreadDelete,
  onThreadRename,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  searchTerm = '',
  onSearchChange,
  isSearching = false,
  hideHeader = false,
  roles = [],
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const rolesMap = useMemo(() => new Map(roles.map(r => [r.id, r])), [roles]);
  const listRef = useRef(null);

  // Estado para el menú contextual
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedThreadForMenu, setSelectedThreadForMenu] = useState(null);

  // Estado para hover (solo desktop)
  const [hoveredThreadId, setHoveredThreadId] = useState(null);

  // Estado para el dialog de renombrar
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');

  // Refs for scroll handler — always reads fresh values, no stale closures
  const hasMoreRef = useRef(hasMore);
  const isLoadingMoreRef = useRef(isLoadingMore);
  const onLoadMoreRef = useRef(onLoadMore);
  hasMoreRef.current = hasMore;
  isLoadingMoreRef.current = isLoadingMore;
  onLoadMoreRef.current = onLoadMore;

  // Infinite scroll: load more when user scrolls near the bottom.
  // The actual scroll container is the sidebar's scrollable Box (ancestor with overflowY:auto),
  // NOT listRef — ThreadList grows with content inside the sidebar's scroll area.
  useEffect(() => {
    const list = listRef.current;
    if (!list || !onLoadMore) return;

    // Walk up the DOM to find the nearest scrollable ancestor
    let scrollParent = list.parentElement;
    while (scrollParent && scrollParent.scrollHeight <= scrollParent.clientHeight) {
      scrollParent = scrollParent.parentElement;
    }
    // Fallback to direct parent if nothing scrolls yet (initial render)
    if (!scrollParent) scrollParent = list.parentElement;

    const handleScroll = () => {
      if (!hasMoreRef.current || isLoadingMoreRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollParent;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        onLoadMoreRef.current();
      }
    };

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [onLoadMore]);

  // Handlers para el menú contextual
  const handleContextMenu = (event, thread) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('🖱️ [ThreadList] Click derecho en thread:', thread.title);

    setSelectedThreadForMenu(thread);
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleMenuIconClick = (event, thread) => {
    event.stopPropagation();
    console.log('⋮ [ThreadList] Click en menú de opciones:', thread.title);

    setSelectedThreadForMenu(thread);
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedThreadForMenu(null);
  };

  const handleRenameFromMenu = () => {
    if (selectedThreadForMenu) {
      console.log('✏️ [ThreadList] Renombrando thread:', selectedThreadForMenu.title);
      setRenameValue(selectedThreadForMenu.title);
      setRenameDialogOpen(true);
    }
    // Solo cerrar el menú contextual, NO limpiar selectedThreadForMenu
    // ya que lo necesitamos para el diálogo de renombrar
    setContextMenu(null);
  };

  const handleDeleteFromMenu = () => {
    if (selectedThreadForMenu && onThreadDelete) {
      console.log('🗑️ [ThreadList] Eliminando thread:', selectedThreadForMenu.title);
      onThreadDelete(selectedThreadForMenu.id);
    }
    handleCloseContextMenu();
  };

  // Handlers para el dialog de renombrar
  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setRenameValue('');
    setRenameError('');
    setIsRenaming(false);
    setSelectedThreadForMenu(null);
  };

  const handleRenameConfirm = async () => {
    if (!selectedThreadForMenu || !onThreadRename || !renameValue.trim()) {
      console.warn('[ThreadList] Falta información para renombrar');
      return;
    }

    try {
      setIsRenaming(true);
      setRenameError('');
      console.log('[ThreadList] Renombrando thread:', selectedThreadForMenu.id, 'a:', renameValue.trim());

      await onThreadRename(selectedThreadForMenu.id, renameValue.trim());

      console.log('[ThreadList] Thread renombrado exitosamente');
      handleRenameDialogClose();
    } catch (error) {
      console.error('[ThreadList] Error al renombrar thread:', error);
      setRenameError(error.message || 'Error al renombrar el chat');
      setIsRenaming(false);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: '100%', 
        maxWidth: 300, 
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {!hideHeader && (
        <Box sx={{
          px: 1.5,
          pt: 0.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75
        }}>
          {/* Header: Title + New Conversation Button */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="subtitle2" component="div">
              {t('conversationsTitle')}
            </Typography>
            <Tooltip title={t('newConversation')} placement="bottom">
              <IconButton
                onClick={() => onThreadSelect(null)}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  ...hoverable({ backgroundColor: alpha(theme.palette.primary.main, 0.08) })
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Campo de búsqueda */}
          {onSearchChange && (
            <TextField
              fullWidth
              size="small"
              placeholder={t('searchConversations', 'Buscar conversaciones...')}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              inputProps={{ inputMode: 'search', enterKeyHint: 'search', autoCapitalize: 'none' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    {isSearching ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => onSearchChange('')}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ) : null
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.paper
                }
              }}
            />
          )}
        </Box>
      )}
      <List
        ref={listRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          pt: 0,  // Remove default List padding top
          pb: 2,  // Padding bottom to separate last conversation from divider
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.background.default,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'light' ? '#888' : '#555',
            borderRadius: '4px',
            '&:hover': {
              background: theme.palette.mode === 'light' ? '#555' : '#666',
            },
          },
        }}>
        {threads.map((thread) => {
          const isHovered = hoveredThreadId === thread.id;
          const isSelected = thread.id === currentThreadId;

          return (
            <ListItem
              key={thread.id}
              disablePadding
              onContextMenu={(e) => handleContextMenu(e, thread)}
              onMouseEnter={() => setHoveredThreadId(thread.id)}
              onMouseLeave={() => setHoveredThreadId(null)}
              sx={{ position: 'relative' }}
            >
              {/* Icono de tres puntos */}
              <IconButton
                size="small"
                onClick={(e) => handleMenuIconClick(e, thread)}
                aria-label={t('threadList.menuOptions', { threadTitle: thread.title })}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  opacity: {
                    xs: 1, // Mobile/Tablet: always visible
                    md: (isHovered || isSelected) ? 1 : 0 // Desktop: visible on hover or when selected
                  },
                  transition: 'opacity 0.2s ease-in-out',
                  bgcolor: 'background.paper',
                  ...hoverable({ bgcolor: 'action.hover' }),
                  minWidth: { xs: 44, md: 32 }, // Larger touch target on mobile/tablet
                  minHeight: { xs: 44, md: 32 }, // Larger touch target on mobile/tablet
                  zIndex: 1
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <ListItemButton
                selected={isSelected}
                onClick={() => onThreadSelect(thread)}
                sx={{
                  py: 0.5, // Compact vertical padding (4px) for dense layout
                  paddingRight: '48px',
                  paddingLeft: '13px', // consistent alignment (3px stripe + 13px = 16px)
                  position: 'relative',
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.13),
                    ...hoverable({ backgroundColor: alpha(theme.palette.primary.main, 0.2) })
                  },
                }}
              >
                {/* Colored left stripe for agent threads, with tooltip */}
                {(() => {
                  const role = thread.metadata?.rolId ? rolesMap.get(thread.metadata.rolId) : null;
                  const stripe = (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        bgcolor: role ? role.color : 'transparent',
                        borderRadius: '0 2px 2px 0',
                      }}
                    />
                  );
                  return role ? (
                    <Tooltip title={role.name} placement="right" enterDelay={200} arrow>
                      {stripe}
                    </Tooltip>
                  ) : stripe;
                })()}
                {thread.isUnreadByDaemon && (
                  <Box
                    sx={{
                      minWidth: 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.error.main,
                      marginRight: 1.5,
                      flexShrink: 0
                    }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip
                    title={thread.title}
                    arrow
                    enterDelay={300}
                    enterTouchDelay={0}
                    placement="right"
                  >
                    <Typography
                      noWrap
                      sx={{
                        maxWidth: '220px', // Increased from 180px - shows more text
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {thread.title}
                    </Typography>
                  </Tooltip>
                  {thread.createdAt && (
                    <Tooltip title={formatTimestampFull(thread.createdAt)} arrow>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.6875rem', // Increased from 0.65rem (10.4px → 11px)
                          color: 'text.secondary', // Changed from text.disabled for better contrast
                          display: 'block'
                        }}
                      >
                        {formatTimestamp(thread.createdAt)}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          );
        })}

        {/* Indicador de carga */}
        {isLoadingMore && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Mensaje cuando no hay más threads */}
        {!hasMore && threads.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('allConversationsLoaded', 'Todas las conversaciones cargadas')}
            </Typography>
          </Box>
        )}
      </List>

      {/* Menú contextual */}
      <Menu
        open={!!contextMenu}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={handleRenameFromMenu}
          sx={{
            gap: 1.5,
            minWidth: 180,
            ...hoverable({ backgroundColor: 'action.hover' }),
          }}
        >
          <EditIcon fontSize="small" color="primary" />
          <Typography>{t('threadList.contextMenu.rename', 'Renombrar')}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteFromMenu}
          sx={{
            gap: 1.5,
            minWidth: 180,
            ...hoverable({
              backgroundColor: 'error.light',
              '& .MuiTypography-root': { color: 'error.contrastText' },
              '& .MuiSvgIcon-root': { color: 'error.contrastText' },
            }),
          }}
        >
          <DeleteIcon fontSize="small" color="error" />
          <Typography color="error.main">{t('threadList.contextMenu.delete', 'Eliminar')}</Typography>
        </MenuItem>
      </Menu>

      {/* Dialog para renombrar */}
      <Dialog
        open={renameDialogOpen}
        onClose={isRenaming ? undefined : handleRenameDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('threadList.renameDialog.title', 'Renombrar chat')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('threadList.renameDialog.label', 'Título del chat')}
            type="text"
            fullWidth
            variant="outlined"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            inputProps={{ inputMode: 'text', autoCapitalize: 'sentences', enterKeyHint: 'done' }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && renameValue.trim() && !isRenaming) {
                handleRenameConfirm();
              }
            }}
            disabled={isRenaming}
            error={!!renameError}
            helperText={renameError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameDialogClose} disabled={isRenaming}>
            {t('threadList.renameDialog.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            disabled={!renameValue.trim() || isRenaming}
          >
            {isRenaming ? t('threadList.renameDialog.renaming', 'Renombrando...') : t('threadList.renameDialog.confirm', 'Renombrar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ThreadList; 